import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formConfigs } from "./formConfigs";
import "./AddNewForm.css"; 
import MultiSelect from "../designSystem/MultiSelect";
import SingleSelect from "../designSystem/SingleSelect";
import PrimaryButton from "../subComponents/buttons/PrimaryButton";
import CancelButton from "../subComponents/buttons/CustomCancelButton";
import { Check } from "lucide-react";
import Textarea from "../designSystem/TextArea";
import TextField from "../designSystem/TextField";
import { validateFields } from "../../utils/Validations";
import { t } from "../../locales/translate";

function AddNewForm({ itemName, onClose, onSave, editData }) {
  const dispatch = useDispatch();
  const config = formConfigs[itemName];
  const [formValues, setFormValues] = useState(config.initialValues);
  
  const [formErrors, setFormErrors] = useState({});
  const permissions = JSON.parse(localStorage.getItem('permissionsList')) || [];
  const canAdd = !config.requiredPermission || permissions.includes(config.requiredPermission);
  
  const dropdownOptions = {};
  config.fields.forEach((field) => {
    if ((field.type === "select" || field.type === "multiselect") && field.optionsSelector) {
      dropdownOptions[field.name] = useSelector(field.optionsSelector);
    }
  });

  useEffect(() => {
    if (config.fetchDependencies) {
      config.fetchDependencies(dispatch);
    }
  }, [dispatch, config]);

  useEffect(() => {
    if (editData) {
      setFormValues(editData);
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateFields(formValues, config.validateKey);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const action = editData ? config.updateAction : config.addAction;
      if (action) {
        dispatch(action(formValues)).then(response => {
           if (onSave) {
             
             const savedData = response?.payload?.result?.[0] || response || formValues;
             onSave(savedData);
           }
        });
      }
      onClose();
    }
  };

  const handleClose = () => {
    setFormErrors({});
    setFormValues(config.initialValues);
    onClose();
  };

  return (
    <form className="addnewform-container" onSubmit={handleSubmit}>
      {config.fields.map((field) => (
        <div key={field.name} className="addnewform-group">
          {field.type !== "checkbox" && <label>{field.label}</label>}

          {field.type === "text" && (
            <TextField
              type="text"
              name={field.name}
              value={formValues[field.name] || ""}
              onChange={handleChange}
              error={formErrors[field.name]}
            />
          )}

          {field.type === "textarea" && (
            <Textarea
              name={field.name}
              value={formValues[field.name] || ""}
              onChange={handleChange}
              placeholder={field.label}
              width="365px"
              error={formErrors[field.name]}
            />
          )}

          {field.type === "select" && (
            <SingleSelect
              isSearchable
              options={dropdownOptions[field.name] || []}
              value={formValues[field.name] || ""}
              onChange={(selectedOption) => {
                setFormValues(prev => {
                  const updatedValues = {
                    ...prev,
                    [field.name]: selectedOption?.value || "",
                  };

                  if (field.name === 'cityId' && selectedOption) {
                    updatedValues.cityName = selectedOption.label;
                  }
                  
                  return updatedValues;
                });
              }}
              placeholder={`${field.label}`}
              error={formErrors[field.name]}
            />
          )}

          {field.type === "multiselect" && (
            <MultiSelect
              isSearchable
              options={dropdownOptions[field.name] || []}
              value={formValues[field.name] || []}
              onChange={(selectedValues) => {
                setFormValues(prev => ({
                  ...prev,
                  [field.name]: selectedValues || [],
                }));
              }}
              placeholder={`${field.label}`}
              error={formErrors[field.name]}
            />
          )}

          {field.type === "checkbox" && (
            <label className="checkbox-label">
              <input
                type="checkbox"
                name={field.name}
                checked={!!formValues[field.name]}
                onChange={handleChange}
              />
              <span>{field.label}</span>
            </label>
          )}
        </div>
      ))}

      <div className="addnewform-footer">
        <CancelButton type="button" text={t("cancel")} width={document.body.dir === "rtl" ? "119px" : ""}  onClick={handleClose} />
        <PrimaryButton
          type="submit"
          text={t("save")}
          icon={Check}
          width={document.body.dir === "rtl" ? "123px" : ""}
        />
      </div>
    </form>
  );
}

export default AddNewForm;